if (!customElements.get('countdown-timer')) {
  class CountdownTimer extends HTMLElement {
    constructor() {
      super();
      this.endDate = new Date(this.dataset.endDate).getTime();
      if (!Number(this.endDate)) return;

      // check if reset_counter is enabled
      this.resetCounter = this.dataset.resetCounter === 'true';

      // Horizon doesn't ship Symmetry's initLazyScript helper — start immediately.
      // If a parent scrolling banner wraps us, skip init so it can coordinate.
      if (!this.closest('.marquee-item')) {
        if (typeof window.initLazyScript === 'function') {
          window.initLazyScript(this, this.init.bind(this));
        } else {
          this.init();
        }
      }
    }

    init() {
      this.daysEl = this.querySelector('.js-days');
      this.daysPart1El = this.querySelector('.js-days .countdown__number-part-1');
      this.daysPart2El = this.querySelector('.js-days .countdown__number-part-2');
      this.hoursPart1El = this.querySelector('.js-hours .countdown__number-part-1');
      this.hoursPart2El = this.querySelector('.js-hours .countdown__number-part-2');
      this.minsPart1El = this.querySelector('.js-mins .countdown__number-part-1');
      this.minsPart2El = this.querySelector('.js-mins .countdown__number-part-2');
      this.secsPart1El = this.querySelector('.js-secs .countdown__number-part-1');
      this.secsPart2El = this.querySelector('.js-secs .countdown__number-part-2');

      // If the grid wasn't rendered (hide_on_end + past), bail.
      if (!this.daysEl) {
        this.setAttribute('loaded', '');
        return;
      }

      this.second = 1000;
      this.minute = 60 * this.second;
      this.hour = 60 * this.minute;
      this.day = 24 * this.hour;

      const numberTransitionDuration = getComputedStyle(this).getPropertyValue('--countdown-number-transition-duration');
      if (numberTransitionDuration) {
        this.numberTransitionDurationMS = CountdownTimer.durationToMS(numberTransitionDuration);
        this.animated = this.numberTransitionDurationMS > 0;
      }

      // add transition elements
      if (this.animated) {
        this.querySelectorAll('.countdown__number:is(.js-days, .js-hours, .js-mins, .js-secs) .countdown__number-part').forEach((el) => {
          el.innerHTML = '';
          const span1 = document.createElement('span');
          span1.className = 'countdown__number-part-current';
          span1.textContent = '0';
          const span2 = document.createElement('span');
          span2.className = 'countdown__number-part-previous';
          span2.textContent = '0';
          el.append(span1, span2);
        });
      }

      this.timer();
      this.interval = setInterval(this.timer.bind(this), this.second);
      this.setAttribute('loaded', '');
    }

    disconnectedCallback() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    }

    timer() {
      const timeDiff = this.endDate - new Date();

      if (timeDiff < 0) {
        if (this.resetCounter) {
          // Jump forward to the next future midnight instantly (+24hr roll-over).
          // 86400000ms === 24 * 60 * 60 * 1000 === this.day
          const daysToAdd = Math.ceil(Math.abs(timeDiff) / this.day);
          this.endDate = this.endDate + (daysToAdd * this.day);
          // Fall through to update the display immediately
        } else {
          this.setAttribute('loaded', '');
          return;
        }
      }

      const days = Math.floor(timeDiff / this.day);
      const hours = Math.floor(timeDiff / this.hour);
      const mins = Math.floor(timeDiff / this.minute);
      const secs = Math.floor(timeDiff / this.second);

      // Days: zero-pad to 2 digits for visual parity with hrs/mins/secs.
      // If days >= 100, overflow into part1 (e.g. "123" → "12" / "3") to preserve layout.
      const daysRaw = String(days);
      let displayDays;
      if (daysRaw.length <= 2) {
        displayDays = daysRaw.padStart(2, '0');
      } else {
        displayDays = daysRaw.slice(0, -1) + daysRaw.slice(-1);
      }
      // Hide DAYS column + its trailing separator when days === 0
      const daysItem = this.daysEl.closest('.countdown__item');
      const daysSep = this.querySelector('.js-sep-days');
      if (days === 0) {
        daysItem.classList.add('countdown__item--is-0');
        if (daysSep) daysSep.style.display = 'none';
      } else {
        daysItem.classList.remove('countdown__item--is-0');
        if (daysSep) daysSep.style.display = '';
      }
      this.setPartValue(this.daysPart1El, displayDays.slice(0, -1), true);
      this.setPartValue(this.daysPart2El, displayDays.slice(-1));

      const displayHours = String(hours - days * 24).padStart(2, '0');
      this.setPartValue(this.hoursPart1El, displayHours[0], true);
      this.setPartValue(this.hoursPart2El, displayHours[1]);

      const displayMins = String(mins - hours * 60).padStart(2, '0');
      this.setPartValue(this.minsPart1El, displayMins[0], true);
      this.setPartValue(this.minsPart2El, displayMins[1]);

      const displaySecs = String(secs - mins * 60).padStart(2, '0');
      this.setPartValue(this.secsPart1El, displaySecs[0], true);
      this.setPartValue(this.secsPart2El, displaySecs[1]);

      this.setAttribute('loaded', '');
    }

    setPartValue(part, value, setZeroClass) {
      if (setZeroClass) {
        part.classList.toggle('countdown__number-part--is-0', value === '0');
      }

      // Changing value and animated
      if (this.animated) {
        const current = part.querySelector('.countdown__number-part-current');
        if (value !== current.textContent) {
          const previous = part.querySelector('.countdown__number-part-previous');
          part.classList.add('countdown__number-part--start-transition');
          previous.textContent = current.textContent;
          current.textContent = value;
          setTimeout(() => {
            requestAnimationFrame(() => part.classList.remove('countdown__number-part--start-transition'));
          }, 20);
        }
      } else {
        part.textContent = value;
      }
    }

    static durationToMS(duration) {
      if (duration.indexOf('ms') > 0) {
        return parseInt(duration, 10);
      }

      if (duration.indexOf('s') > 0) {
        return parseFloat(duration) * 1000;
      }

      throw new Error(`Duration unit not recognised: ${duration}`);
    }
  }

  customElements.define('countdown-timer', CountdownTimer);
}
